import axios from "../axios";

const API_BASE = "/projects";

/**
 * Map image type to image_content hash
 * @param {string} imageType - One of the valid image types
 * @returns {Object} image_content hash object
 */
export const getImageContentFromType = (imageType) => {
  // Extract base type from formats like "external_with_label_a_1w" -> "external_with_label"
  const baseType = imageType.replace(/_[a-z]_1w$/, "");
  
  const imageContentMap = {
    external_with_label: {
      internal: true,
      external: true,
      height_width: true,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    internal_with_height_and_width: {
      internal: true,
      external: false,
      height_width: false,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    // Keep old types for backward compatibility
    room_name_with_internal: {
      internal: true,
      external: false,
      height_width: false,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    room_name_with_external: {
      internal: false,
      external: true,
      height_width: false,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    room_name_with_depth: {
      internal: false,
      external: false,
      height_width: false,
      depth: true,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    room_name_with_internal_dimension: {
      internal: true,
      external: false,
      height_width: true,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    room_name_with_external_dimension: {
      internal: false,
      external: true,
      height_width: true,
      depth: false,
      shutter_dimension: false,
      label: true,
      panel_label: true,
      colors: true,
    },
    room_name_with_shutter_dimension: {
      internal: false,
      external: true,
      height_width: true,
      depth: false,
      shutter_dimension: true,
      label: true,
      panel_label: true,
      colors: true,
    },
  };

  return imageContentMap[baseType] || {
    internal: false,
    external: false,
    height_width: false,
    depth: false,
    shutter_dimension: false,
    label: false,
    panel_label: false,
    colors: false,
  };
};

/**
 * Delete a room render and all its images
 * @deprecated This function is no longer used as the backend doesn't support DELETE route
 * @param {number} projectId
 * @param {number} roomId
 * @returns {Promise<{message: string}>}
 */
export const deleteRoomRender = async (projectId, roomId) => {
  // This function is deprecated - backend doesn't support DELETE route
  // Keeping for backward compatibility but it will not be called
  throw new Error("deleteRoomRender is deprecated - backend doesn't support DELETE route");
};

/**
 * Create/Reset a room render - creates a new one or reuses existing
 * @param {number} projectId
 * @param {number} roomId
 * @param {boolean} forceNew - Kept for API compatibility but no longer deletes existing room render (backend doesn't support DELETE route)
 * @returns {Promise<{message: string, room_render_id: number, status: string}>}
 * 
 * Behavior:
 * - Creates a new room render or reuses existing one (backend handles this)
 * - If a room render doesn't exist, creates a new one with status 'pending'
 * - If a room render already exists, backend will handle it appropriately
 * - The backend POST endpoint manages room render creation/updates
 */
export const startAutoCapture = async (projectId, roomId, forceNew = false) => {
  // Note: forceNew parameter is kept for API compatibility but DELETE call is removed
  // as the backend doesn't support DELETE route for room_render
  // The backend POST endpoint will handle creating/updating room renders appropriately
  
  const response = await axios.post(
    `${API_BASE}/${projectId}/rooms/${roomId}/room_render`,
    {}
  );
  return response;
};

/**
 * Upload image for a room render
 * @param {number} projectId
 * @param {number} roomId
 * @param {string} imageType - One of: room_name_with_internal, room_name_with_external, room_name_with_depth, room_name_with_internal_dimension, room_name_with_external_dimension, room_name_with_shutter_dimension
 * @param {Blob|File} imageFile - The image file to upload
 * @param {Object} [imageContent] - Optional image_content hash. If not provided, will be derived from imageType
 * @returns {Promise<{message: string, image_id: number, image_type: string, all_captured: boolean, status: string}>}
 */
export const uploadImage = async (projectId, roomId, imageType, imageFile, imageContent = null) => {
  const formData = new FormData();
  formData.append("image_type", imageType);
  formData.append("image", imageFile);

  // Use provided imageContent or derive from imageType
  let content = imageContent || getImageContentFromType(imageType);
  
  // Extract view information from imageType to make each view unique
  // Format: "external_with_label_a_1w" -> view: "front", suffix: "a"
  // This ensures each view (a, b, c, d, t) has unique image_content
  const viewMatch = imageType.match(/_([a-z])_1w$/);
  if (viewMatch && viewMatch[1]) {
    const suffixToView = { a: "front", b: "right", c: "back", d: "left", t: "top" };
    const suffix = viewMatch[1];
    const cameraView = suffixToView[suffix];
    if (cameraView) {
      // Add camera_view to image_content to make each view unique
      content = { ...content, camera_view: cameraView };
    }
  }
  
  formData.append("image_content", JSON.stringify(content));

  const response = await axios.post(
    `${API_BASE}/${projectId}/rooms/${roomId}/room_render/upload_image`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response;
};

/**
 * Download ZIP file containing all captured images
 * @param {number} projectId
 * @param {number} roomId
 * @returns {Promise<void>}
 */
export const downloadZip = async (projectId, roomId) => {
  try {
    const response = await axios.get(
      `${API_BASE}/${projectId}/rooms/${roomId}/room_render/download_zip`,
      {
        responseType: "blob",
      }
    );

    // The axios interceptor returns response.data directly.
    // For blob responses, response.data is already a Blob, but the interceptor
    // might return it directly or wrapped. Handle both cases.
    let blob;
    if (response instanceof Blob) {
      // Interceptor returned the blob directly
      blob = response;
    } else if (response && response.data instanceof Blob) {
      // Interceptor returned an object with data property
      blob = response.data;
    } else {
      // Fallback: create blob from response
      blob = new Blob([response], { type: "application/zip" });
    }

    // Check if blob is actually an error (JSON error responses are sometimes returned as blobs)
    if (blob.type === "application/json" || blob.size < 100) {
      const text = await blob.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.message || "Failed to download ZIP file");
      } catch (e) {
        // If not JSON, check if it's a small error message
        if (blob.size < 100) {
          throw new Error(text || "Failed to download ZIP file");
        }
      }
    }

    // Verify it's a valid ZIP file (ZIP files start with PK)
    if (blob.size === 0) {
      throw new Error("Downloaded file is empty");
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `room_render_${roomId}.zip`;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);
    } catch (error) {
      throw error;
    }
};

/**
 * Get room render images for a specific room
 * @param {number} projectId
 * @param {number} roomId
 * @returns {Promise<Array>} Array of room render images
 */
export const getRoomRenderImages = async (projectId, roomId) => {
  const response = await axios.get(
    `${API_BASE}/${projectId}/rooms/${roomId}/room_render/images`
  );
  return response;
};

/**
 * Get all room render images for all rooms in a project
 * @param {number} projectId
 * @returns {Promise<Object>} Object mapping room IDs (as strings) to their render images arrays
 */
export const getAllRoomRenderImages = async (projectId) => {
  const url = `${API_BASE}/${projectId}/room_renders/images`;
  try {
    const response = await axios.get(url);
    // Return response.data if axios wraps it, otherwise return response
    return response.data || response;
  } catch (error) {
    console.error("getAllRoomRenderImages: Error:", error);
    throw error;
  }
};

/**
 * Download ZIP file containing all room renders for a project
 * @param {number} projectId
 * @param {string} projectName - Optional project name for the filename
 * @returns {Promise<void>}
 */
export const downloadAllRoomRenders = async (projectId, projectName = null) => {
  try {
    const response = await axios.get(
      `${API_BASE}/${projectId}/room_renders/download_zip`,
      {
        responseType: "blob",
      }
    );

    // The axios interceptor returns response.data directly.
    // For blob responses, response.data is already a Blob, but the interceptor
    // might return it directly or wrapped. Handle both cases.
    let blob;
    if (response instanceof Blob) {
      // Interceptor returned the blob directly
      blob = response;
    } else if (response && response.data instanceof Blob) {
      // Interceptor returned an object with data property
      blob = response.data;
    } else {
      // Fallback: create blob from response
      blob = new Blob([response], { type: "application/zip" });
    }

    // Check if blob is actually an error (JSON error responses are sometimes returned as blobs)
    if (blob.type === "application/json" || blob.size < 100) {
      const text = await blob.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.message || "Failed to download ZIP file");
      } catch (e) {
        // If not JSON, check if it's a small error message
        if (blob.size < 100) {
          throw new Error(text || "Failed to download ZIP file");
        }
      }
    }

    // Verify it's a valid ZIP file
    if (blob.size === 0) {
      throw new Error("Downloaded file is empty");
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = projectName 
      ? `room_renders_${projectName.replace(/[^a-z0-9]/gi, '_')}.zip`
      : `room_renders_${projectId}.zip`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);
  } catch (error) {
    throw error;
  }
};

